import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

export const iosColors = {
  canvas: "#F7F8FA",
  surface: "#FFFFFF",
  ink: "#0F0F12",
  muted: "#55575C",
  subtle: "#6E7278",
  line: "rgba(15,15,18,0.14)",
  red: "#A51812",
  orange: "#B54708",
  green: "#147A5C",
  shadow: "rgba(15,15,18,0.05)",
};

export function IosPage({ children }) {
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: iosColors.canvas,
        color: iosColors.ink,
        px: 1.25,
        py: 1.2,
        "& .MuiTypography-root": {
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        },
        "& .MuiChip-root": {
          height: "auto",
          minHeight: 24,
        },
        "& .MuiChip-label": {
          display: "block",
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          textOverflow: "clip",
          py: 0.25,
        },
        "& .MuiButton-root": {
          whiteSpace: "normal",
          overflowWrap: "anywhere",
        },
      }}
    >
      {children}
    </Box>
  );
}

export function IosSectionHeader({ eyebrow, title, subtitle, action }) {
  return (
    <Box sx={{ mb: 1.2 }}>
      <Box sx={{ width: 28, height: 2, borderRadius: 999, bgcolor: iosColors.orange, mb: 0.8 }} />
      {eyebrow ? (
        <Typography sx={{ color: iosColors.subtle, fontSize: 10.75, fontWeight: 900, letterSpacing: 1.2, textTransform: "uppercase", mb: 0.42 }}>
          {eyebrow}
        </Typography>
      ) : null}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.75}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 22.5, fontWeight: 780, lineHeight: 1.1, color: iosColors.ink }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ color: iosColors.muted, fontSize: 13.25, lineHeight: 1.32, mt: 0.42 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Stack>
    </Box>
  );
}

export function IosCard({ children, sx }) {
  return (
    <Box
      sx={{
        bgcolor: iosColors.surface,
        border: `1px solid ${iosColors.line}`,
        borderRadius: 1,
        boxShadow: "none",
        p: 1.25,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function IosWorkflowCard({ icon, title, subtitle, detail, onClick, accent = "orange", chips, disabled = false }) {
  const accentColor = accent === "red" ? iosColors.red : accent === "green" ? iosColors.green : iosColors.orange;
  return (
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      sx={{ display: "block", width: "100%", textAlign: "left", borderRadius: 1, opacity: disabled ? 0.62 : 1 }}
    >
      <IosCard sx={{ width: "100%", minHeight: 76, p: 1.2 }}>
        <Stack direction="row" spacing={1.05} alignItems="flex-start">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 0.9,
              bgcolor: `${accentColor}14`,
              color: accentColor,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 22 }}>{icon}</Icon>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ color: iosColors.ink, fontSize: 15.75, fontWeight: 850, lineHeight: 1.18, overflowWrap: "anywhere" }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography sx={{ color: accentColor, fontSize: 11.75, fontWeight: 850, mt: 0.3, textTransform: "uppercase" }}>
                {subtitle}
              </Typography>
            ) : null}
            {detail ? (
              <Typography
                sx={{
                  color: iosColors.muted,
                  fontSize: 13.25,
                  lineHeight: 1.3,
                  mt: 0.4,
                  overflowWrap: "anywhere",
                }}
              >
                {detail}
              </Typography>
            ) : null}
            {chips?.length ? (
              <Stack direction="row" spacing={0.45} sx={{ mt: 0.65, flexWrap: "wrap", rowGap: 0.45 }}>
                {chips.slice(0, 3).map((chip) => (
                  <Chip
                    key={chip}
                    label={chip}
                    size="small"
                    sx={{ height: 24, bgcolor: "#F2F3F5", color: iosColors.muted, fontSize: 11.75, fontWeight: 800 }}
                  />
                ))}
              </Stack>
            ) : null}
          </Box>
          <Icon sx={{ color: iosColors.subtle, fontSize: 20, mt: 0.05 }}>chevron_right</Icon>
        </Stack>
      </IosCard>
    </ButtonBase>
  );
}

export function IosList({ children, sx }) {
  return (
    <Box
      sx={{
        bgcolor: iosColors.surface,
        border: `1px solid ${iosColors.line}`,
        borderRadius: 1,
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function IosListItem({ icon, title, meta, trailing, onClick, accent = "orange", disabled = false }) {
  const accentColor = accent === "red" ? iosColors.red : accent === "green" ? iosColors.green : iosColors.orange;
  return (
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: "100%",
        display: "block",
        textAlign: "left",
        opacity: disabled ? 0.62 : 1,
        "&:not(:last-of-type)": { borderBottom: `1px solid ${iosColors.line}` },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.05} sx={{ minHeight: 67, px: 1.2, py: 0.95 }}>
        {icon ? (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 0.8,
              bgcolor: `${accentColor}12`,
              color: accentColor,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 22 }}>{icon}</Icon>
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: iosColors.ink, fontSize: 15.75, lineHeight: 1.18, fontWeight: 850 }}>
            {title}
          </Typography>
          {meta ? (
            <Typography sx={{ color: iosColors.muted, fontSize: 12.9, lineHeight: 1.25, mt: 0.35 }}>
              {meta}
            </Typography>
          ) : null}
        </Box>
        {trailing ? (
          <Typography
            sx={{
              color: iosColors.muted,
              fontSize: 12.9,
              fontWeight: 800,
              textAlign: "right",
              maxWidth: 104,
              overflowWrap: "anywhere",
              flexShrink: 0,
            }}
          >
            {trailing}
          </Typography>
        ) : null}
        <Icon sx={{ color: iosColors.subtle, fontSize: 20, flexShrink: 0 }}>chevron_right</Icon>
      </Stack>
    </ButtonBase>
  );
}

export function IosFilterBar({ label, value, options, onChange }) {
  const selectedOption = options.find((option) => option.key === value);
  const compact = options.length <= 3;
  return (
    <Box sx={{ mt: 1.05, mb: 1.2 }}>
      {label ? (
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: 0.65, px: 0.25 }}>
          <Typography sx={{ color: iosColors.muted, fontSize: 12.25, fontWeight: 900, textTransform: "uppercase" }}>
            {label}
          </Typography>
          {selectedOption ? (
            <Typography sx={{ color: iosColors.ink, fontSize: 12.25, fontWeight: 900, textAlign: "right" }}>
              {selectedOption.label}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
      <Box
        sx={{
          overflowX: "auto",
          pb: 0.35,
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack direction="row" spacing={0.75} sx={{ minWidth: compact ? "100%" : "max-content" }}>
          {options.map((option) => {
            const selected = value === option.key;
            const accentColor =
              option.accent === "red" ? iosColors.red : option.accent === "green" ? iosColors.green : option.accent === "orange" ? iosColors.orange : iosColors.ink;
            return (
              <ButtonBase
                key={option.key}
                onClick={() => onChange(option.key)}
                sx={{
                  flex: compact ? 1 : "0 0 auto",
                  minWidth: compact ? 0 : 94,
                  minHeight: 62,
                  px: 1,
                  py: 0.8,
                  borderRadius: 1,
                  border: `1px solid ${selected ? `${accentColor}66` : iosColors.line}`,
                  bgcolor: selected ? "#fff" : "rgba(255,255,255,0.72)",
                  color: selected ? accentColor : iosColors.ink,
                  textAlign: "center",
                  boxShadow: selected ? `inset 0 -3px 0 ${accentColor}` : "none",
                }}
              >
                <Stack spacing={0.35} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>
                  {option.icon ? (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: 0.9,
                        bgcolor: selected ? `${accentColor}14` : "rgba(15,15,18,0.04)",
                      }}
                    >
                      <Icon sx={{ fontSize: 19.5 }}>{option.icon}</Icon>
                    </Box>
                  ) : null}
                  <Typography sx={{ fontSize: 13.25, fontWeight: 900, lineHeight: 1.05, maxWidth: "100%" }}>
                    {option.label}
                  </Typography>
                </Stack>
              </ButtonBase>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}

IosPage.propTypes = { children: PropTypes.node.isRequired };
IosSectionHeader.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  action: PropTypes.node,
};
IosSectionHeader.defaultProps = { eyebrow: "", subtitle: null, action: null };
IosCard.propTypes = { children: PropTypes.node.isRequired, sx: PropTypes.shape({}) };
IosCard.defaultProps = { sx: {} };
IosWorkflowCard.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  detail: PropTypes.node,
  onClick: PropTypes.func.isRequired,
  accent: PropTypes.oneOf(["orange", "red", "green"]),
  chips: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
};
IosWorkflowCard.defaultProps = { subtitle: null, detail: null, accent: "orange", chips: [], disabled: false };
IosList.propTypes = { children: PropTypes.node.isRequired, sx: PropTypes.shape({}) };
IosList.defaultProps = { sx: {} };
IosListItem.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.node.isRequired,
  meta: PropTypes.node,
  trailing: PropTypes.node,
  onClick: PropTypes.func.isRequired,
  accent: PropTypes.oneOf(["orange", "red", "green"]),
  disabled: PropTypes.bool,
};
IosListItem.defaultProps = { icon: null, meta: null, trailing: null, accent: "orange", disabled: false };
IosFilterBar.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      accent: PropTypes.oneOf(["orange", "red", "green"]),
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};
IosFilterBar.defaultProps = { label: "" };
